import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check, Globe2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useBranding } from "@/context/BrandingContext";
import { useLandingContent } from "@/context/LandingContentContext";
import FlagIcon from "@/components/ui/FlagIcon";
import { SUPPORTED_LANGUAGES, setStoredLanguage } from "@/i18n";

const OFFICE_BG = "/login-office.jpg";

/**
 * AuthShell — shared split-card layout used by Login, ForgotPassword and
 * ResetPassword. Provides the dotted-globe backdrop, the office scene on the
 * left (lg+ only), the blue gradient panel on the right, and a top-right
 * language picker. Children are rendered inside the blue panel.
 */
export default function AuthShell({ children, testId = "auth-shell" }) {
  const { brand } = useBranding();
  const cms = useLandingContent();
  const cmsSlot = cms.images?.login_office;
  const cmsUrl = cmsSlot?.asset_id
    ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/landing-settings/asset/${cmsSlot.asset_id}`
    : null;
  const heroImage = cmsUrl || brand.brand_login_image_url || OFFICE_BG;
  const objectFit = cmsSlot?.object_fit || "contain";
  const objectPosition = cmsSlot?.object_position || "50% 50%";
  const alt = cmsSlot?.alt || brand.brand_name;
  return (
    <div
      className="min-h-screen relative bg-white overflow-hidden flex items-center justify-center px-4 py-8 sm:py-12"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      data-testid={testId}
    >
      <GlobeBackdrop />
      <BackToLandingLink />
      <div
        className="relative w-full max-w-5xl rounded-[1.75rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(15,98,254,0.35),0_0_0_1px_rgba(15,98,254,0.08)] bg-white grid lg:grid-cols-2"
        data-testid="auth-card"
      >
        {/* Left: office scene (portrait 4:5, no cropping) */}
        <div className="hidden lg:block relative bg-slate-50">
          <div className="aspect-[4/5] w-full">
            <img
              src={heroImage}
              alt={alt}
              className="w-full h-full"
              style={{ objectFit, objectPosition }}
              data-testid="auth-office-image"
            />
          </div>
        </div>

        {/* Right: blue panel with content */}
        <div
          className="relative text-white p-8 sm:p-10 lg:p-12 flex flex-col justify-center"
          data-testid="auth-panel"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-br from-[#0F62FE] via-[#0038B8] to-[#001A66]"
          />
          <div
            aria-hidden="true"
            className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-white/10 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#4098FF]/25 blur-3xl"
          />

          <div className="absolute top-4 right-4 z-10">
            <AuthLanguagePicker />
          </div>

          <div className="relative">
            {/* Mobile brand — only visible when the office image is hidden */}
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center overflow-hidden">
                {brand.brand_logo_url ? (
                  <img src={brand.brand_logo_url} alt="logo" className="w-full h-full object-contain" />
                ) : (
                  <Globe2 className="w-6 h-6" />
                )}
              </div>
              <div className="font-bold text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {brand.brand_name}
              </div>
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function BackToLandingLink() {
  const { t } = useTranslation();
  // Rendered outside the auth card so it never overlaps the language
  // picker or the form. Fixed at the top-left on every viewport,
  // safely inside the safe area on notched phones.
  return (
    <Link
      to="/"
      data-testid="auth-back-to-landing"
      className="fixed z-50 top-3 left-3 sm:top-5 sm:left-5 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white/85 hover:bg-white text-[#0F62FE] text-xs sm:text-sm font-bold border border-white/60 backdrop-blur shadow-lg shadow-[#0F62FE]/10 transition-colors"
    >
      <ArrowLeft className="w-4 h-4" />
      <span>{t("auth.back_to_landing", { defaultValue: "Back to Landing Page" })}</span>
    </Link>
  );
}

function GlobeBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <svg
        viewBox="0 0 400 400"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute top-1/2 left-[-100px] -translate-y-1/2 w-[700px] h-[700px] opacity-[0.05] text-[#0F62FE]"
      >
        {[0, 30, 60, 90, 120, 150].map((r) => (
          <ellipse
            key={r}
            cx="200"
            cy="200"
            rx={180 * Math.abs(Math.cos((r * Math.PI) / 180))}
            ry="180"
            fill="none"
            stroke="currentColor"
            strokeWidth="0.7"
          />
        ))}
        {[-60, -30, 0, 30, 60].map((y) => (
          <ellipse
            key={y}
            cx="200"
            cy={200 + y * 1.8}
            rx={180 * Math.cos((y * Math.PI) / 180)}
            ry="8"
            fill="none"
            stroke="currentColor"
            strokeWidth="0.7"
          />
        ))}
      </svg>
      <span className="absolute top-[8%] left-[35%] w-2 h-2 rounded-full bg-[#0F62FE]/25" />
      <span className="absolute top-[12%] right-[8%] w-2.5 h-2.5 rounded-full bg-[#4098FF]/20" />
      <span className="absolute top-[18%] right-[30%] w-1.5 h-1.5 rounded-full bg-[#0F62FE]/30" />
      <span className="absolute bottom-[10%] left-[10%] w-3 h-3 rounded-full bg-cyan-300/30" />
      <span className="absolute bottom-[18%] right-[14%] w-2 h-2 rotate-45 bg-[#4098FF]/25" />
      <span className="absolute top-[35%] left-[8%] text-[#0F62FE]/25 text-2xl font-bold">+</span>
      <span className="absolute bottom-[8%] right-[42%] text-[#4098FF]/25 text-xl font-bold">+</span>
      <span className="absolute top-[7%] right-[36%] text-[#0F62FE]/20 text-lg font-bold">+</span>
      <div className="absolute top-[6%] left-[42%] w-72 h-72 rounded-full bg-pink-100/40 blur-3xl" />
      <div className="absolute bottom-[6%] right-[30%] w-80 h-80 rounded-full bg-cyan-100/40 blur-3xl" />
    </div>
  );
}

function AuthLanguagePicker() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];
  return (
    <div className="relative" data-testid="auth-language-picker">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur border border-white/25 text-white text-xs font-semibold transition-colors"
        data-testid="auth-language-toggle"
        aria-label={current.label}
      >
        <FlagIcon code={current.flag} />
        <ChevronDown className="w-3.5 h-3.5 text-white/85" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1.5 w-44 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden z-50"
          data-testid="auth-language-menu"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setStoredLanguage(l.code); setOpen(false); }}
              className={`flex items-center gap-3 w-full px-3 py-2 text-sm text-left hover:bg-slate-50 ${
                l.code === current.code ? "bg-blue-50 text-[#0F62FE] font-semibold" : "text-slate-700"
              }`}
              data-testid={`auth-language-${l.code}`}
            >
              <FlagIcon code={l.flag} />
              <span className="flex-1">{l.label}</span>
              {l.code === current.code && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
